import {
  Stack,
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Checkbox,
} from '@mui/material'
import { RfhColors } from '../../config/florimixColors'
import { TransactionStatusCheck } from './TransactionStatusCheck'
import { TransactionDeleteDialog } from './TransactionDeleteDialog'
import { TransactionStatus, type KlantTransactie } from '../../types/api'
import { useState } from 'react'
import {
  ArrowRightIcon,
  ArrowDownIcon,
  EditIcon,
  DeleteIcon,
} from '@floriday/floriday-icons'
import { deleteTransaction } from '../../services/transactionService'
import { useQueryClient } from '@tanstack/react-query'
import { useNavigate } from '@tanstack/react-router'
import { useTranslation } from 'react-i18next'
import { mapTransactionTypeLabel } from '../../utils/transactionTypeMapper'
import { useOptionalTransactionSelection } from '../../context/transactionSelectionContext'
import { formatUtcDateTime } from '../../utils/transactionDate'

type TransactionListItemProps = {
  transaction: KlantTransactie
  index: number
  isExpanded: boolean
  onExpandedChange: (isExpanded: boolean) => void
}

export const TransactionListItem = ({
  transaction,
  index,
  isExpanded,
  onExpandedChange,
}: TransactionListItemProps) => {
  const { t } = useTranslation()
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false)
  const backgroundColor =
    index % 2 === 1 ? RfhColors.lightGrey : RfhColors.white
  const queryClient = useQueryClient()
  const navigate = useNavigate()
  const selection = useOptionalTransactionSelection()

  const handleEditClick = (e: React.MouseEvent) => {
    e.stopPropagation()
    if (!transaction.id) return
    navigate({
      to: '/collecting',
      search: { mode: 'edit', transactionId: transaction.id },
    })
  }

  const handleDeleteClick = (e: React.MouseEvent) => {
    e.stopPropagation()
    setIsDeleteDialogOpen(true)
  }

  const handleDelete = async (transactionId: string) => {
    await deleteTransaction(transactionId)
    queryClient.invalidateQueries({ queryKey: ['transactions'] })
  }

  return (
    <Stack
      component='li'
      onClick={() => onExpandedChange(!isExpanded)}
      sx={{
        gap: 1,
        position: 'relative',
        padding: theme => theme.spacing(1, 2, 1, selection ? 7 : 2),
        backgroundColor,
        cursor: 'pointer',
      }}
    >
      <Box
        sx={{
          gap: 2,
          display: 'flex',
          flexDirection: 'row',
          alignItems: 'center',
        }}
      >
        {selection && transaction.id && (
          <Checkbox
            checked={selection.isTransactionSelected(transaction.id)}
            onClick={event => event.stopPropagation()}
            onChange={() =>
              selection.toggleTransaction(transaction.id as string)
            }
            sx={{
              position: 'absolute',
              left: theme => theme.spacing(0.5),
              top: '50%',
              transform: 'translateY(-50%)',
            }}
            slotProps={{
              input: {
                'aria-label': t('transactions.selection.selectTransaction', {
                  transactionId: transaction.id,
                }),
              },
            }}
          />
        )}
        <Typography
          variant='h3'
          sx={{
            fontWeight: 700,
          }}
        >{`#${transaction.id}`}</Typography>
        <TransactionStatusCheck status={transaction.status} />
      </Box>
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'row',
          justifyContent: 'space-between',
        }}
      >
        <Typography variant='body1'>
          {`${transaction.locatie} - ${mapTransactionTypeLabel(transaction.transactieType, t)}`}
        </Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Typography
            variant='body1'
            sx={{
              fontWeight: 700,
              color: RfhColors.darkGrey,
            }}
          >
            {t('transactions.codesCount', {
              count: transaction.fustAantallen
                ? transaction.fustAantallen.length
                : 0,
            })}
          </Typography>
          {isExpanded ? (
            <ArrowDownIcon fontSize='small' />
          ) : (
            <ArrowRightIcon fontSize='small' />
          )}
        </Box>
      </Box>
      <Typography variant='body1' color={RfhColors.darkGrey}>
        {formatUtcDateTime(transaction.datum)}
      </Typography>
      {isExpanded && (
        <Box>
          <TableContainer
            component={Paper}
            sx={{ backgroundColor, paddingBottom: 1 }}
          >
            <Table size='small'>
              <TableHead>
                <TableRow>
                  <TableCell>{t('transactions.table.code')}</TableCell>
                  <TableCell>{t('transactions.table.stw')}</TableCell>
                  <TableCell>{t('transactions.table.stpl')}</TableCell>
                  <TableCell>{t('transactions.table.stk')}</TableCell>
                  <TableCell>{t('transactions.table.total')}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {transaction.fustAantallen?.map((fust, index) => (
                  <TableRow key={`${index}-${fust.fustCode}`}>
                    <TableCell>{fust.fustCode}</TableCell>
                    <TableCell>{fust.stapelwagens}</TableCell>
                    <TableCell>{fust.stapels}</TableCell>
                    <TableCell>{fust.stuks}</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>
                      {fust.totaal}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
          {transaction.status === TransactionStatus.New && (
            <Stack
              direction='row'
              sx={{
                justifyContent: 'flex-end',
                gap: 4,
              }}
            >
              <IconButton
                size='small'
                onClick={handleEditClick}
                sx={{ paddingY: 2 }}
              >
                <EditIcon />
              </IconButton>
              <IconButton
                size='small'
                onClick={handleDeleteClick}
                sx={{ paddingY: 2 }}
              >
                <DeleteIcon sx={{ color: RfhColors.alertRed, fontSize: 28 }} />
              </IconButton>
            </Stack>
          )}
        </Box>
      )}
      <TransactionDeleteDialog
        isOpen={isDeleteDialogOpen}
        onClose={() => setIsDeleteDialogOpen(false)}
        transactionId={transaction.id}
        onDelete={handleDelete}
      />
    </Stack>
  )
}
